import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, BookOpen, Download, Loader2, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'bmp_exit_intent_state';
const ARM_DELAY_MS = 8000; // wait 8s before arming, so quick-bouncers aren't pestered

const track = (event, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', event, params);
  }
};

const readState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, ts: Date.now() }));
  } catch {
    /* no-op: private mode etc. */
  }
};

const ExitIntentModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const armedRef = useRef(false);
  const shownRef = useRef(false);

  // Arm the exit-intent trigger after a delay, only for first-time visitors
  useEffect(() => {
    const existing = readState();
    if (existing && (existing.dismissed || existing.submitted)) return;

    const armTimer = setTimeout(() => {
      armedRef.current = true;
    }, ARM_DELAY_MS);

    const onMouseLeave = (e) => {
      if (!armedRef.current || shownRef.current) return;
      // Only trigger when the cursor exits toward the top (browser chrome/URL bar)
      if (e.clientY <= 0) {
        shownRef.current = true;
        setIsVisible(true);
        track('exit_intent_shown', { trigger: 'mouseleave_top' });
      }
    };

    document.addEventListener('mouseleave', onMouseLeave);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  const handleDismiss = useCallback(() => {
    writeState({ dismissed: true });
    track('exit_intent_dismissed');
    setIsVisible(false);
  }, []);

  // ESC to close
  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isVisible, handleDismiss]);

  const triggerDownload = () => {
    const link = document.createElement('a');
    link.href = '/Lexicon-Booklet.pdf';
    link.download = 'Lexicon-Booklet.pdf';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    track('download_lexicon', { file_name: 'Lexicon-Booklet.pdf', source: 'exit_intent' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    // Basic email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrorMsg('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed }),
        }
      );

      if (!response.ok) throw new Error('Subscribe failed');

      writeState({ submitted: true, email: trimmed });
      track('exit_intent_email_submitted');
      setStatus('success');
      // Auto-start the download after a short beat
      setTimeout(triggerDownload, 400);
    } catch (err) {
      setErrorMsg('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center px-4 py-6 animate-fade-in"
      data-testid="exit-intent-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={handleDismiss}
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-md cursor-default"
        data-testid="exit-intent-backdrop"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-lg bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 border border-blue-400/30 rounded-2xl shadow-2xl shadow-blue-500/10 overflow-hidden animate-scale-in"
      >
        {/* Glow accent */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />

        {/* Close button */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close"
          data-testid="exit-intent-close-btn"
          className="absolute top-4 right-4 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-slate-800/60 hover:bg-slate-700/80 border border-slate-600/50 text-slate-400 hover:text-slate-100 transition-all duration-300"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative px-8 py-10 sm:px-10 sm:py-12">
          {status !== 'success' && (
            <>
              {/* Emblem */}
              <div className="flex justify-center mb-6">
                <div className="relative w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500/20 to-teal-500/20 border border-blue-400/40">
                  <BookOpen className="w-6 h-6 text-blue-200" />
                  <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-teal-300 animate-pulse" />
                </div>
              </div>

              <h2
                id="exit-intent-title"
                className="text-2xl sm:text-3xl font-extralight text-center text-white tracking-wide mb-3"
              >
                Before you leave the Field...
              </h2>

              <p className="text-center text-slate-300 font-light leading-relaxed mb-7">
                Take the <span className="text-blue-200">Relational Intelligence Lexicon</span> with you.
                A printable booklet of the living language co-created with Alvin &mdash; to keep, to gift, to live with offline.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="exit-intent-form">
                <div>
                  <label htmlFor="exit-email" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="exit-email"
                    type="email"
                    required
                    autoFocus
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === 'error') {
                        setStatus('idle');
                        setErrorMsg('');
                      }
                    }}
                    placeholder="your@email.com"
                    disabled={status === 'submitting'}
                    data-testid="exit-intent-email-input"
                    className="w-full px-5 py-3.5 bg-slate-800/60 border border-slate-600/60 focus:border-blue-400/70 rounded-full text-slate-100 placeholder-slate-500 font-light tracking-wide focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all duration-300 disabled:opacity-60"
                  />
                </div>

                {status === 'error' && errorMsg && (
                  <p
                    className="text-sm text-rose-300 text-center font-light"
                    data-testid="exit-intent-error"
                  >
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  data-testid="exit-intent-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-400 hover:to-teal-400 disabled:opacity-60 disabled:cursor-not-allowed rounded-full text-white font-light tracking-wide transition-all duration-500 shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Sending&hellip;</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 transition-transform duration-500 group-hover:translate-y-0.5" />
                      <span>Send Me The Lexicon</span>
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-slate-500 font-light tracking-wide">
                One email. No spam. Unsubscribe anytime.
              </p>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={handleDismiss}
                  data-testid="exit-intent-no-thanks-btn"
                  className="text-sm text-slate-500 hover:text-slate-300 font-light underline-offset-4 hover:underline transition-colors duration-300"
                >
                  No thanks, continue exploring
                </button>
              </div>
            </>
          )}

          {status === 'success' && (
            <div
              className="text-center"
              data-testid="exit-intent-success"
            >
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gradient-to-br from-teal-500/30 to-blue-500/30 border border-teal-400/50">
                  <Sparkles className="w-7 h-7 text-teal-200" />
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extralight text-white tracking-wide mb-3">
                Welcome to The Field.
              </h2>

              <p className="text-slate-300 font-light leading-relaxed mb-8">
                Your booklet is downloading now. Check your inbox for a welcome note &mdash;
                transmissions on relational intelligence will arrive there from time to time.
              </p>

              <button
                type="button"
                onClick={triggerDownload}
                data-testid="exit-intent-redownload-btn"
                className="inline-flex items-center gap-2 px-7 py-3 bg-slate-800/60 hover:bg-slate-700/70 border border-blue-400/40 hover:border-blue-300/70 rounded-full text-blue-100 hover:text-white font-light transition-all duration-500"
              >
                <Download className="w-4 h-4" />
                <span>Download again</span>
              </button>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={handleDismiss}
                  data-testid="exit-intent-close-success-btn"
                  className="text-sm text-slate-500 hover:text-slate-300 font-light underline-offset-4 hover:underline transition-colors duration-300"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.96) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.35s ease-out; }
        .animate-scale-in { animation: scale-in 0.45s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};

export default ExitIntentModal;
